import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Breadcrump from "../../components/Breadcrump";
import SEO from "../../components/SEO";
import categoryAPI from "../../api/categoryApi";
import CategoryCard from "./Card";
import ProductCard from "../products/Card";
import { normalizeProduct } from "../../utils/normalizeProduct";
import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "../homepage/Shared/CustomSwiper";

gsap.registerPlugin(ScrollTrigger);

// ─── SVG: Hero Corner Ornament ────────────────────────────────────────────────
const HeroOrnament = () => (
  <svg
    className="catdetail-hero-ornament"
    viewBox="0 0 90 90"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="1"
      y="1"
      width="88"
      height="88"
      stroke="white"
      strokeWidth="0.8"
      strokeDasharray="4 3"
    />
    <rect x="9" y="9" width="72" height="72" stroke="white" strokeWidth="0.5" />
    <circle cx="45" cy="45" r="16" stroke="white" strokeWidth="0.6" />
    <line x1="45" y1="9" x2="45" y2="29" stroke="white" strokeWidth="0.5" />
    <line x1="45" y1="61" x2="45" y2="81" stroke="white" strokeWidth="0.5" />
    <line x1="9" y1="45" x2="29" y2="45" stroke="white" strokeWidth="0.5" />
    <line x1="61" y1="45" x2="81" y2="45" stroke="white" strokeWidth="0.5" />
    <circle cx="45" cy="45" r="2.5" fill="white" opacity="0.5" />
  </svg>
);

// ─── Divider ─────────────────────────────────────────────────────────────────
const Divider = () => (
  <div className="catdetail-divider">
    <div className="catdetail-divider-rule" />
    <div className="catdetail-divider-diamond" />
    <div className="catdetail-divider-rule" />
  </div>
);

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = ({ message }) => (
  <div className="catdetail-empty">
    <svg
      className="catdetail-empty-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8" strokeLinecap="round" />
    </svg>
    <p className="catdetail-empty-title">Nothing to show</p>
    <p className="catdetail-empty-sub">{message}</p>
  </div>
);

// ─── Hero Skeleton ────────────────────────────────────────────────────────────
const HeroSkeleton = () => <div className="catdetail-hero-skeleton" />;

// ─── Sub Skeletons ────────────────────────────────────────────────────────────
const SubSkeletons = () => (
  <div className="row g-3">
    {Array.from({ length: 4 }).map((_, i) => (
      <div className="col-md-3 col-6" key={i}>
        <div className="catdetail-sub-skeleton" />
      </div>
    ))}
  </div>
);

// ─── Product Skeletons ────────────────────────────────────────────────────────
const ProductSkeletons = () => (
  <div className="catdetail-product-grid">
    {Array.from({ length: 8 }).map((_, i) => (
      <div className="catdetail-product-skeleton" key={i} />
    ))}
  </div>
);

// ─── Detail Page ─────────────────────────────────────────────────────────────
function DetailPage() {
  const { "*": slug } = useParams();

  const [item, setItem] = useState({});
  const [subcategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── GSAP Refs ──
  const heroEyebrowRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroDescRef = useRef(null);
  const subEyebrowRef = useRef(null);
  const subHeadingRef = useRef(null);
  const subRuleRef = useRef(null);
  const prodEyebrowRef = useRef(null);
  const prodTitleRef = useRef(null);
  const prodRuleRef = useRef(null);
  const prodGridRef = useRef(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!slug) return;
    fetchCategoryDetail();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const fetchCategoryDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await categoryAPI.details(slug);
      if (res.code === 200) {
        const result = res.data;
        setItem(result?.category || {});
        setSubCategories(result?.subcategories || []);
        setProducts(result?.products || []);
        setMetaData(result?.category?.meta || {});
      } else {
        setError("Category not found.");
      }
    } catch (err) {
      console.error("Error loading category:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── GSAP Animations ────────────────────────────────────────────────────────
  useEffect(() => {
    if (loading || !item?.name) return;

    const ctx = gsap.context(() => {
      // Hero entrance
      const heroTl = gsap.timeline({ defaults: { ease: "power3.out" } });
      if (heroEyebrowRef.current)
        heroTl.to(
          heroEyebrowRef.current,
          { opacity: 1, y: 0, duration: 0.65 },
          0,
        );
      if (heroTitleRef.current)
        heroTl.to(
          heroTitleRef.current,
          { opacity: 1, y: 0, duration: 0.8 },
          0.15,
        );
      if (heroDescRef.current)
        heroTl.to(heroDescRef.current, { opacity: 1, duration: 0.7 }, 0.38);

      // Scroll-triggered headings
      [
        subEyebrowRef,
        subHeadingRef,
        subRuleRef,
        prodEyebrowRef,
        prodTitleRef,
        prodRuleRef,
      ].forEach((ref) => {
        if (!ref.current) return;
        gsap.to(ref.current, {
          opacity: 1,
          y: 0,
          duration: 0.65,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ref.current,
            start: "top 86%",
            once: true,
          },
        });
      });

      // Product cards stagger
      if (prodGridRef.current) {
        const cards = prodGridRef.current.children;
        if (cards.length > 0) {
          gsap.from(cards, {
            opacity: 0,
            y: 28,
            duration: 0.65,
            stagger: 0.07,
            ease: "power3.out",
            scrollTrigger: {
              trigger: prodGridRef.current,
              start: "top 82%",
              once: true,
            },
          });
        }
      }
    });

    return () => ctx.revert();
  }, [loading, item?.name, products.length]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Breadcrump title={item?.name || "Category"} />
      <SEO meta={metaData} />
      {/* <section className="catdetail-hero-section" aria-label="Category Hero">
      </section> */}

      {subcategories && subcategories.length > 0 && (
        <section className="catdetail-sub-section" aria-label="Sub Collections">
          <div className="container">
            <div className="catdetail-sub-header">
              <div>
                {/* <p className="catdetail-sub-eyebrow" ref={subEyebrowRef}>
                  <span
                    className="catdetail-sub-eyebrow-line"
                    aria-hidden="true"
                  />
                  Browse
                </p> */}
                <h2 className="catdetail-sub-heading" ref={subHeadingRef}>
                  Sub <em>Categories</em>
                </h2>
                <div
                  className="catdetail-sub-rule"
                  ref={subRuleRef}
                  aria-hidden="true"
                />
              </div>

              {!loading && subcategories.length > 0 && (
                <span className="catdetail-products-count">
                  {subcategories.length}{" "}
                  {subcategories.length === 1 ? "collection" : "collections"}
                </span>
              )}
            </div>

            {loading ? (
              <SubSkeletons />
            ) : (
              <Swiper
                className="catdetail-sub-swiper"
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={16}
                pagination={{ clickable: true }}
                autoplay={{ delay: 3200, disableOnInteraction: false }}
                loop={subcategories.length > 3}
                breakpoints={{
                  0: { slidesPerView: 1.6 },
                  480: { slidesPerView: 2.2 },
                  768: { slidesPerView: 3 },
                  1024: { slidesPerView: 3 },
                }}
              >
                {subcategories.map((sub, i) => (
                  <SwiperSlide key={sub._id || sub.id || i}>
                    <CategoryCard item={sub} featured={false} variant="list" />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>
        </section>
      )}

      {subcategories.length > 0 && products.length > 0 && <Divider />}

      {/* ══════════════════════════════
          PRODUCTS
      ══════════════════════════════ */}
      {products && products.length > 0 ? (
        <section className="catdetail-products-section" aria-label="Products">
          <div className="container">
            <div className="catdetail-products-header">
              <div>
                {/* <p className="catdetail-products-eyebrow" ref={prodEyebrowRef}>
                  <span
                    className="catdetail-products-eyebrow-line"
                    aria-hidden="true"
                  />
                  Curated Picks
                </p> */}
                <h2 className="catdetail-products-title" ref={prodTitleRef}>
                  Our <em>Products</em>
                </h2>
                <div
                  className="catdetail-products-rule"
                  ref={prodRuleRef}
                  aria-hidden="true"
                />
              </div>

              {!loading && products.length > 0 && (
                <span className="catdetail-products-count">
                  {products.length} {products.length === 1 ? "item" : "items"}
                </span>
              )}
            </div>
            {/* Loading */}
            {loading && <ProductSkeletons />}

            {/* Error */}
            {!loading && error && <EmptyState message={error} />}

            {/* Empty */}
            {!loading && !error && products.length === 0 && (
              <EmptyState message="No products in this category yet. Check back soon!" />
            )}

            {/* Grid */}
            {!loading && !error && products.length > 0 && (
              <div className="catdetail-product-grid" ref={prodGridRef}>
                {products.map((product, i) => (
                  <ProductCard
                    key={product._id || product.id || i}
                    product={normalizeProduct(product)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      ) : (
        // Sirf tab EmptyState dikhao jab subcategories BHI empty hon
        // Agar subcategories hain toh products empty state skip karo
        subcategories.length === 0 && (
          <div className="text-center py-5">
            <img
              src={`${process.env.PUBLIC_URL}/images/blank_image.png`}
              alt="No subcategories"
              className="w-50 mb-3"
            />
            <h5>No subcategories found in "{item?.name}"</h5>
          </div>
        )
      )}
    </>
  );
}

export default DetailPage;
